import { AppImages } from "app/appImages";
import { CorePermissions } from "app/appPermissions";
import { GenericAbortSignal } from "axios";
import { MprcConfirmDialog } from "components";
import { AutoCompleteDataModel, AutoCompleteSuggestions, MprcAutoComplete } from "components/MprcAutoComplete";
import { createAutoCompleteModel } from "components/MprcAutoComplete/AutoCompleteDataModel";
import { enqueueSnackbar } from "notistack";
import { isCanceledError, useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    label?: string;
    placeholder?: string;
    selectedItems: AutoCompleteDataModel[];
    multiSelect: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
    disabled?: boolean;
    startAdorment?: React.ReactNode;
    onChange: (item: AutoCompleteDataModel[]) => void;
    onError: (error: any) => void;
    onNewTag?: (item: AutoCompleteDataModel) => void;
}


////////////////////////////////////////////////////////////////////////////

/**
 * Auto complete de usuários
 * @param props
 */
export const UserTagInterestAutoComplete: React.FC<IProps> = (props) => {

    const api = useCoreApi();
    const { t } = useTranslation();
    const { permissionExplorer } = useSession();

    const [dialog, setDialog] = useState<any>();

    const { multiSelect, placeholder, selectedItems, label, error, helperText, disabled = false, onChange, startAdorment, onError, onNewTag } = props;

    ///
    const getSuggestions = (searchText: string, pageNumber: number, pageSize: number, trySelectedHistory: boolean, signal: GenericAbortSignal | undefined): Promise<AutoCompleteSuggestions<AutoCompleteDataModel>> => {
        searchText = searchText.toLowerCase();

        return new Promise<AutoCompleteSuggestions<AutoCompleteDataModel>>((resolve) => {
            api.v1.suggestions.userTagInterests({
                searchText: searchText,
                pageNumber: pageNumber,
                pageSize: pageSize,
            }, { signal })
                .then(result => {
                    resolve({
                        items: result.data.items as AutoCompleteDataModel[],
                        totalRows: result.data.totalRows
                    });
                })
                .catch(error => {
                    if (!isCanceledError(error)) {
                        resolve({ items: [], totalRows: 0 });
                        onError(error);
                    }
                });
        });
    };

    // Inclui uma nova tag na lista e salva.
    const handleNewTag = (tag: string) => {
        var item = createAutoCompleteModel(v4(), tag);

        if (onNewTag) onNewTag(item);
        setDialog(null);
    };


    // Solicita confirmação incluir uma nova tag.
    const handleKeyReturn = (value: string) => {
        if (!onNewTag || !permissionExplorer.hasOne([CorePermissions.TagsPersonalInterestCreate])) return;

        if (value.length > 255) {
            enqueueSnackbar(t("Messages.MaxStringLimit", { ns: "shared", count: 255 }), { variant: "error" });
            return;
        }
        if (value.length > 0) {
            setDialog(
                <MprcConfirmDialog
                    open={true}
                    message={t("User.UserTagInterestAutoComplete.ConfirmAddMessage", { name: value })}
                    onClose={(confirm) => { if (confirm) handleNewTag(value); else setDialog(null); }}
                />
            );
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {dialog}
            <MprcAutoComplete
                label={label}
                multiSelect={multiSelect}
                placeholder={placeholder}
                selectedItems={selectedItems}
                disabled={disabled}
                emptyThumbnail={AppImages.UserThumbnail}
                onGetSuggestions={getSuggestions}
                onChange={onChange}
                error={error}
                helperText={helperText}
                startAdornment={startAdorment}
                onKeyReturn={handleKeyReturn}
            />
        </>
    );
};
